import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import styled from 'styled-components';
import { FiSearch, FiX } from 'react-icons/fi';
import 'flag-icons/css/flag-icons.min.css';

const Overlay = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 1rem;
`;

const Modal = styled(motion.div)`
  background: white;
  border-radius: 20px;
  padding: 2rem;
  max-width: 600px;
  width: 100%;
  max-height: 80vh;
  overflow-y: auto;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1.5rem;
`;

const Title = styled.h3`
  margin: 0;
  color: #334155;
  font-size: 1.3rem;
  flex: 1;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 0.5rem;
  border-radius: 8px;
  transition: all 0.2s;
  
  &:hover {
    background: #f1f5f9;
  }
`;

const SearchContainer = styled.div`
  position: relative;
  margin-bottom: 1.5rem;
`;

const LetterFilterContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(13, 1fr);
  gap: 0.5rem;
  margin-bottom: 1.5rem;
`;

const LetterButton = styled(motion.button)`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  border: 2px solid ${props => props.active ? '#ffb53d' : '#e2e8f0'};
  background: ${props => props.active ? '#ffb53d' : 'white'};
  color: ${props => props.active ? 'white' : '#334155'};
  font-weight: 600;
  font-size: 0.9rem;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &:hover {
    border-color: #ffb53d;
    background: ${props => props.active ? '#e29606' : '#fff4e6'};
    transform: scale(1.1);
  }
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 0.75rem 1rem 0.75rem 2.5rem;
  border: 2px solid #e2e8f0;
  border-radius: 12px;
  font-size: 1rem;
  background: #f8fafc;
  transition: all 0.2s;
  
  &:focus {
    outline: none;
    border-color: #ffb53d;
    background: white;
  }
`;

const SearchIcon = styled(FiSearch)`
  position: absolute;
  left: 0.75rem;
  top: 50%;
  transform: translateY(-50%);
  color: #64748b;
  font-size: 1.2rem;
`;

const CountriesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 1rem;
  max-height: 400px;
  overflow-y: auto;
  padding-right: 0.5rem;
  
  /* Custom scrollbar */
  &::-webkit-scrollbar {
    width: 8px;
  }
  
  &::-webkit-scrollbar-track {
    background: #f1f5f9;
    border-radius: 4px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: #ffb53d;
    border-radius: 4px;
  }
`;

const ContinentSection = styled.div`
  &:not(:last-child) {
    margin-bottom: 1rem;
  }
`;

const ContinentHeader = styled.h4`
  color: #3b82f6;
  font-size: 1.1rem;
  margin: 0 0 0.75rem 0;
  padding-bottom: 0.5rem;
  border-bottom: 2px solid #3b82f6;
  font-weight: 600;
`;

const ContinentCountries = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
  gap: 0.75rem;
`;

const CountryButton = styled(motion.button)`
  background: white;
  border: 2px solid #e2e8f0;
  border-radius: 12px;
  padding: 1.25rem;
  display: flex;
  align-items: center;
  gap: 1rem;
  cursor: pointer;
  transition: all 0.2s;
  text-align: left;
  
  &:hover {
    border-color: #ffb53d;
    background: #fff4e6;
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(255, 181, 61, 0.2);
  }
`;

const CountryFlag = styled.span`
  width: 36px;
  height: 27px;
  border-radius: 4px;
  overflow: hidden;
  display: inline-block;
  flex-shrink: 0;
  
  &.fi {
    background-size: cover;
    background-position: center;
  }
`;

const CountryName = styled.span`
  font-weight: 600;
  color: #334155;
  font-size: 1.1rem;
`;

const RequiredNote = styled(motion.div)`
  background: #fef3c7;
  border: 1px solid #f59e0b;
  border-radius: 8px;
  padding: 0.75rem;
  margin-bottom: 1.5rem;
  color: #92400e;
  font-size: 0.9rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

function CountryModal({ ingredient, countries, onSelectCountry, onClose, isRequired }) {
  const [selectedLetter, setSelectedLetter] = useState(null);

  const allLetters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('');

  const filteredCountries = selectedLetter
    ? countries.filter(country => country.name.charAt(0).toUpperCase() === selectedLetter).sort((a, b) => a.name.localeCompare(b.name))
    : countries.sort((a, b) => a.name.localeCompare(b.name));

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget && !isRequired) {
      onClose();
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Escape' && !isRequired) {
      onClose();
    }
  };

  useEffect(() => {
    document.addEventListener('keydown', handleKeyPress);
    return () => document.removeEventListener('keydown', handleKeyPress);
  }, [isRequired]);

  return (
    <Overlay
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.2 }}
      onClick={handleOverlayClick}
    >
      <Modal
        initial={{ scale: 0.8, opacity: 0, y: 50 }}
        animate={{ scale: 1, opacity: 1, y: 0 }}
        exit={{ scale: 0.8, opacity: 0, y: 50 }}
        transition={{ duration: 0.3, ease: "easeOut" }}
        onClick={(e) => e.stopPropagation()}
      >
        <Header>
          <Title>
            {isRequired ? 'Select' : 'Change'} country for {ingredient?.name}
          </Title>
          {!isRequired && (
            <CloseButton onClick={onClose}>
              <FiX size={20} />
            </CloseButton>
          )}
        </Header>

        {isRequired && (
          <RequiredNote
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
          >
            ⚠️ Choose a country
          </RequiredNote>
        )}

        <LetterFilterContainer>
          {allLetters.map(letter => (
            <LetterButton
              key={letter}
              active={selectedLetter === letter}
              onClick={() => setSelectedLetter(selectedLetter === letter ? null : letter)}
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.95 }}
            >
              {letter}
            </LetterButton>
          ))}
        </LetterFilterContainer>

        <CountriesGrid>
          <AnimatePresence>
            {filteredCountries.map((country, index) => (
              <CountryButton
                key={country.code}
                onClick={() => onSelectCountry(country)}
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.8 }}
                transition={{ delay: index * 0.03 }}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <CountryFlag className={`fi fi-${country.code}`} />
                <CountryName>{country.name}</CountryName>
              </CountryButton>
            ))}
          </AnimatePresence>
        </CountriesGrid>

      </Modal>
    </Overlay>
  );
}

export default CountryModal;
